import schemas from "@/utils/schemas";
import { yupResolver } from "@hookform/resolvers/yup";
import React, { useLayoutEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Styled } from "./styles";
import { Backdrop, Button } from "@dippi/ui";
import { Grid } from "@mui/material";
import { Forms } from "@/types/form";
import { Brand } from "@/types/entities";
import Base from "./base";
import Description from "./description";
import Socials from "./socials";
import Entities from "./entities";
import constant from "@/utils/constant";
import { useSubmit } from "./hooks";
import FileUpload from "@/components/file-upload";
import { Params, useNavigate, useParams } from "react-router-dom";
import storage from "@/utils/storage";

const getDefaultValues = (data: Brand | undefined): Forms["Brand"] => {
    const values: Forms["Brand"] = {
        email: data?.email ?? "",
        title: data?.title ?? "",
        tag: data?.tag ?? "",
        media: data?.media ?? undefined,
        websiteURL: data?.websiteURL ?? "",
        description: data?.description ?? "",
        socials: data?.socials ?? [],
        entities: data?.entities ?? [],
    };

    return values;
};

interface Props {
    data?: Brand;
}

const BrandForm: React.FC<Props> = ({ data }) => {
    const navigate = useNavigate();

    const { bid, oid } = useParams<Params>();
    const storageBid = storage.getItem("bid");

    const methods = useForm<Forms["Brand"]>({
        resolver: yupResolver(schemas.brand),
        values: getDefaultValues(data),
    });

    const { handleSubmit, formState } = methods;
    const { isDirty, isSubmitting } = formState;

    const { mutateAsync: onSubmit } = useSubmit();
    const SPACING = constant.GRID_SPACING;
    const submitBtnText = bid ? "Update" : "Create";

    const onCancel = () => {
        navigate(`/organization/${oid}`);
        if (storageBid) {
            storage.removeItem("bid");
        }
    };

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <FormProvider {...methods}>
            <Styled.Form
                id="form-brand"
                onSubmit={handleSubmit(async data => await onSubmit(data))}
            >
                <Grid container spacing={SPACING} rowGap={SPACING}>
                    <Grid item container justifyContent="flex-end" gap={SPACING}>
                        <Button variant="outlined" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!isDirty}>
                            {submitBtnText}
                        </Button>
                    </Grid>
                    <Grid container spacing={SPACING} rowGap={SPACING}>
                        <Styled.ImageContainer item lg={3}>
                            <FileUpload />
                        </Styled.ImageContainer>
                        <Grid item container xl={9} gap={SPACING}>
                            <Base />
                            <Description />
                            <Socials />
                            <Entities />
                        </Grid>
                    </Grid>
                </Grid>
                <Backdrop loading={isSubmitting} />
            </Styled.Form>
        </FormProvider>
    );
};

export default BrandForm;
